import ref, { TIMESTAMP } from 'services/firebaseRef';
import { publishEvent } from "services/events";

export const PROFILE_SAVING = 'PROFILE_SAVING';
export const PROFILE_SAVED = 'PROFILE_SAVED';
export const PROFILE_SAVE_ERROR = 'PROFILE_SAVE_ERROR';

export const PROFILE_LOADING = 'PROFILE_LOADING';
export const PROFILE_LOADED = 'PROFILE_LOADED';
export const PROFILE_LOAD_ERROR = 'PROFILE_LOAD_ERROR';

export const PROFILE_ONBOARDINGSTATE_UPDATED = 'PROFILE_ONBOARDINGSTATE_UPDATED';

function profileSaving (userId, data) {
  return {
    type: PROFILE_SAVING,
    userId,
    payload: data
  };
}

function profileSaved (userId, data) {
  return {
    type: PROFILE_SAVED,
    userId,
    payload: data
  };
}

function profileSaveError (error) {
  return {
    type: PROFILE_SAVE_ERROR,
    error
  };
}

function normalizeFormData (data) {
  for (const key of Object.keys(data)) {
    if (typeof data[key] === 'string') {
      data[key] = data[key].trim();
    }
    if (typeof data[key] === 'undefined') {
      data[key] = false;
    }
  }
}

export function saveProfile (userId, registerFormData) {
  return dispatch => {
    const {
      password,
      alert,
      termsAccepted,
      referrerFirstName,
      referrerLastName,
      referrerUniqueUrl,
      referredProgramId,
      ...filteredData
    } = registerFormData;
    return new Promise((resolve, reject) => {
      normalizeFormData(filteredData); // Trim strings
      dispatch(profileSaving(userId, filteredData));
      ref.child('users').child(userId).child('profile')
      .set(filteredData, error => {
        if (error) {
          reject(error);
        } else {
          resolve();
        }
      });
    }).then(() => {
      return new Promise((resolve, reject) => {
        ref.child('users').child(userId).update({
          createdAt: TIMESTAMP,
          updatedAt: TIMESTAMP
        }, error => {
          if (error) {
            reject(error);
          } else {
            resolve();
          }
        });
      });
    }).then(() => {
      publishEvent('Profile Created', {
        ...filteredData,
        // GTM note: required for intercom
        referrerFirstName, referrerLastName, referrerUniqueUrl, referredProgramId
      });
      dispatch(profileSaved(userId, filteredData));
      return filteredData;
    }).catch(error => {
      dispatch(profileSaveError(error));
      throw new Error('Profile save error.');
    });
  };
}

function getCurrentProfile(profileRef) {
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => reject(new Error("Retrieving profile timed out.")), 10000);
    profileRef.once('value', dataSnapshot => {
      clearTimeout(timeout);
      resolve(dataSnapshot.val())
    })
  })
}

function publishOptInEvent(listId, userId, consent, oldProfile, newProfile) {
  if (!oldProfile) return;
  const oldOptIn = oldProfile[`${listId}OptIn`];
  const newOptIn = newProfile[`${listId}OptIn`];
  if (oldOptIn === newOptIn) return;

  const payload = {
    userId,
    email: newProfile.email,
    listId,
    consent,
    location: "profile"
  };
  publishEvent(newOptIn ? 'Subscribed' : 'Unsubscribed', payload);
}

export function updateProfile (userId, formData, sendEvents = true) {
  return dispatch => {
      const {
        alert,
        consent,
        ...filteredData
      } = formData;
      normalizeFormData(filteredData); // Trim strings
      dispatch(profileSaving(userId, filteredData));
      const profileRef = ref.child('users').child(userId).child('profile');
      return Promise.all([
        getCurrentProfile(profileRef),
        profileRef.update(filteredData).then(() => filteredData)
      ]).then((results) => {
        return ref.child('users').child(userId).update({
          updatedAt: TIMESTAMP
        }).then(() => results);
      }).then(([oldProfile, data]) => {
        if (sendEvents) {
          publishEvent('Profile Updated', {
            userId,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            phone: data.phone,
            country: data.country,
            stateOrProvince: data.stateOrProvince,
            cityOrMunicipality: data.cityOrMunicipality,
            school: data.school,
            marketingOptIn: data.marketingOptIn,
            gettingStartedOptIn: data.gettingStartedOptIn,
            directPromotionOptIn: data.directPromotionOptIn
          });
          publishOptInEvent("marketing", userId, "explicit", oldProfile, data);
          publishOptInEvent("directPromotion", userId, "explicit", oldProfile, data);
          publishOptInEvent("gettingStarted", userId, consent || "explicit", oldProfile, data);
        }
        dispatch(profileSaved(userId, data));
        return data;
      }).catch(error => {
        dispatch(profileSaveError(error));
        throw new Error('Profile save error.');
      });
  };
}

function onboardingStateUpdate (userId, onboardingState) {
  return {
    type: PROFILE_ONBOARDINGSTATE_UPDATED,
    userId,
    onboardingState
  };
}

export function updateOnboardingState (userId, onboardingState) {
  return dispatch => {
    return ref.child('users').child(userId).child('profile')
      .update({onboarding: onboardingState})
      .then(() => {
        publishEvent(`Onboarding ${onboardingState}`, {
          userId,
          onboardingState
        });
      })
      .then(() => {
        dispatch(onboardingStateUpdate(userId, onboardingState));
      });
  };
}

function profileLoading () {
  return {
    type: PROFILE_LOADING
  };
}

function profileLoaded (profileData) {
  return {
    type: PROFILE_LOADED,
    profileData
  };
}

function profileLoadError (error) {
  return {
    type: PROFILE_LOAD_ERROR,
    error
  };
}

export function loadProfile (userId) {
  return dispatch => {
    return new Promise((resolve, reject) => {
      dispatch(profileLoading());
      ref.child('users').child(userId)
      .once('value', data => {
        resolve(data.val());
      }, error => {
        reject(error);
      });
    }).then(userData => {
      if (userData && userData.isDeleted) {
        const error = new Error('Profile is deleted.');
        error.code = 'profile/deleted';
        throw error;
      }
      const profileData = userData && userData.profile;
      dispatch(profileLoaded(profileData));
      return profileData;
    }, error => {
      dispatch(profileLoadError(error));
      throw new Error('Profile load error.');
    });
  };
}

export function bindProfile (userId, refs) {
  return dispatch => {
    const profileRef = ref.child('users').child(userId).child('profile');
    profileRef.on('value', snapshot => {
      const profileData = snapshot.val();
      dispatch(profileLoaded(profileData));
    });
    refs.push(profileRef);
  };
}

export function unbindProfile (refs) {
  return () => {
    refs.forEach(savedRef => {
      savedRef.off();
    });
  };
}



// WEBPACK FOOTER //
// ./src/actions/profileActions.js